import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addRechnungPosition,
    RechnungPosition,
    defaultRechnungPosition,
    updateRechnungPosition,
    useRechnungPositionList,
} from 'api/rechnungPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { getArtikelDetailLieferantList } from 'api/artikelDetailLieferant';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { Kunde } from 'api/kunde';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface RechnungPositionEditProps {
    kunde: Kunde;
    rNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const RechnungPositionEdit: React.FC<RechnungPositionEditProps> = ({
    kunde,
    rNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const rechnungPositionList = useRechnungPositionList(
        {
            rNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [rechnungPosition, setRechnungPosition] = React.useState<RechnungPosition | null>(null);

    React.useEffect(() => {
        if (!rechnungPositionList.isLoading && !rechnungPositionList.isFetching && rechnungPosition === null) {
            if (rechnungPositionList.data?.length === 1) {
                setRechnungPosition(cloneDeep(rechnungPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (rechnungPositionList.data[0].artikelGruppeNr && rechnungPositionList.data[0].artikelNr) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: rechnungPositionList.data[0].artikelGruppeNr,
                        artikelNr: rechnungPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setRechnungPosition({ ...defaultRechnungPosition, rNr, pos, posMwst: kunde.land === 'AT' ? 20 : 0 });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [rechnungPosition, rechnungPositionList, rNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !rechnungPositionList.isLoading &&
            !rechnungPositionList.isFetching &&
            rechnungPositionList.data?.length === 1 &&
            rechnungPosition !== null &&
            !isEqual(rechnungPositionList.data[0], rechnungPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('rechnungPosition', rechnungPosition, rechnungPositionList.data[0]);
        }
    }, [savingState, rechnungPositionList, rechnungPosition]);

    React.useEffect(() => {
        if (rechnungPosition) {
            if (rechnungPosition.posMenge && rechnungPosition.posEPreis) {
                const netto = Math.round(rechnungPosition.posMenge * rechnungPosition.posEPreis * 100) / 100;
                const mwstFactor = rechnungPosition.posMwst ? 1 + rechnungPosition?.posMwst / 100 : 1;
                const brutto = rechnungPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setRechnungPosition({
                    ...rechnungPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setRechnungPosition({
                    ...rechnungPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rechnungPosition?.posMenge, rechnungPosition?.posEPreis, rechnungPosition?.posMwst]);

    const validate = (): boolean => {
        if (!rechnungPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (rechnungPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (rechnungPosition.pos === -1) {
                    addRechnungPosition(rechnungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateRechnungPosition(rechnungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof RechnungPosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(rechnungPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (rechnungPosition) {
                    setRechnungPosition({
                        ...rechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof RechnungPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(rechnungPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (rechnungPosition) {
                    setRechnungPosition({
                        ...rechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof RechnungPosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={rechnungPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (rechnungPosition) {
                    setRechnungPosition({
                        ...rechnungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={rechnungPosition?.artikelGruppeNr}
            artikelNr={rechnungPosition?.artikelNr}
            disabled={rechnungPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (rechnungPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setRechnungPosition({
                            ...rechnungPosition,
                            artikelGruppeNr: defaultRechnungPosition.artikelGruppeNr,
                            artikelNr: defaultRechnungPosition.artikelNr,
                            posText: defaultRechnungPosition.posText,
                            posEkPreis: defaultRechnungPosition.posEkPreis,
                            posEPreis: defaultRechnungPosition.posEPreis,
                            posEinh: defaultRechnungPosition.posEinh,
                            lkz: defaultRechnungPosition.lkz,
                            pd: defaultRechnungPosition.pd,
                            pz: defaultRechnungPosition.pz,
                            lNr: defaultRechnungPosition.lNr,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    const artikelDetailLieferanten = (
                        (await getArtikelDetailLieferantList({
                            artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                            artikelNr: newArtikelDetail.artikelNr,
                        })) || []
                    ).sort((a, b) => a.vkGiltAb.localeCompare(b.vkGiltAb));

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const ekPreis = defaultKalkulation?.ek2 || 0;
                    const ePreis = defaultKalkulation?.vk || 0;

                    setRechnungPosition({
                        ...rechnungPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                        lkz: 0,
                        pd: newArtikelDetail.produktGruppeNr,
                        pz: newArtikelDetail.produzentNr,
                        lNr: artikelDetailLieferanten[0]?.lNr,
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof RechnungPosition,
        ekPreisColumn: keyof RechnungPosition,
        ePreisColumn: keyof RechnungPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(rechnungPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek2 || 0;
                    ePreis = kalkulation?.vk || 0;
                }

                if (rechnungPosition) {
                    setRechnungPosition({
                        ...rechnungPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title="Rechnungsposition"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1100}
            onDismiss={() => {
                onClose(false);
            }}
        >
            <form className={formStyles.form}>
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div>{renderArtikelDetailSelect()}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                </div>
                <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                <div className={styles.posLine}>
                    <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                    <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posAMenge', 'A-Menge', { fixed: 3, disabled: true })}</div>
                    <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posNetto', 'VK Netto', { disabled: true })}</div>
                    <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                    <div>{renderDecimalField('posBrutto', 'VK Brutto', { disabled: true })}</div>
                </div>

                <Timestamps
                    modifiedDate={rechnungPosition?.updDat}
                    modifiedDecimalTime={rechnungPosition?.updUhr}
                    modifiedUser={rechnungPosition?.updUser}
                    showCreated={false}
                />

                {validationError && <div className={formStyles.error}>{validationError}</div>}
                {savingState === SavingState.error && <div className={formStyles.error}>Fehler beim Speichern.</div>}

                <DialogFooter>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={savingState === SavingState.notChanged || rechnungPositionList.isLoading}
                        />
                    )}
                </DialogFooter>
            </form>
            {(rechnungPositionList.isLoading || rechnungPositionList.isFetching || !rechnungPosition) && (
                <LoadingIndicator />
            )}
        </Dialog>
    );
};
